import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { AlertTriangle, Router, Package, BrainCircuit, Shield, TrendingUp, Loader2 } from 'lucide-react';

const DetectionAlertsPage = () => {
  const [activeTab, setActiveTab] = useState('router');
  const [selectedLicense, setSelectedLicense] = useState('all');
  const [searchAgent, setSearchAgent] = useState('');

  // 1. Fetch All Agents
  const { data: agentsData = { agents: [] }, isLoading: agentsLoading } = useQuery({
    queryKey: ['allAgents'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/agents');
      // axiosClient handles .data, so we check if agents key exists
      return res?.agents ? res : { agents: res || [] };
    },
  });

  // 2. Fetch Router Alerts
  const { data: routerData = { alerts: [] }, isLoading: routerLoading } = useQuery({
    queryKey: ['adminRouterAlerts'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/alerts/router-attacks');
      return res?.alerts ? res : { alerts: res || [] };
    },
  });

  // 3. Fetch AI Evasion Alerts
  const { data: mlData = { alerts: [] }, isLoading: mlLoading } = useQuery({
    queryKey: ['adminMLAlerts'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/alerts/ai-evasion');
      return res?.alerts ? res : { alerts: res || [] };
    },
  });

  // 4. Fetch Supply Chain Alerts
  const { data: supplyData = { alerts: [] }, isLoading: supplyLoading } = useQuery({
    queryKey: ['adminSupplyAlerts'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/alerts/supply-chain');
      return res?.alerts ? res : { alerts: res || [] };
    },
  });

  // 5. Fetch Blocked/Killed Threats
  const { data: killedData = { threats: [] }, isLoading: killedLoading } = useQuery({
    queryKey: ['adminKilledThreats'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/alerts/killed-threats');
      return res?.threats ? res : { threats: res || [] };
    },
  });

  // ✅ DEFINE AGENTS CONSTANT (Fixes 'agents' is not defined)
  const agents = useMemo(() => agentsData?.agents || [], [agentsData]);

  // ✅ ORGANIZE THREATS BY AGENT
  const threatsByAgent = useMemo(() => {
    const organized = {};

    // Initialize map with all current agents
    agents.forEach(agent => {
      organized[agent.agent_id] = {
        agent_id: agent.agent_id,
        hostname: agent.hostname || 'Unknown Device',
        license: agent.license_package || 'Free Tier',
        router: [],
        ml: [],
        supply: [],
        killed: [],
      };
    });

    // Populate data safely
    (routerData?.alerts || []).forEach(alert => {
      if (organized[alert.agent_id]) organized[alert.agent_id].router.push(alert);
    });

    (mlData?.alerts || []).forEach(alert => {
      if (organized[alert.agent_id]) organized[alert.agent_id].ml.push(alert);
    });

    (supplyData?.alerts || []).forEach(alert => {
      if (organized[alert.agent_id]) organized[alert.agent_id].supply.push(alert);
    });

    (killedData?.threats || []).forEach(threat => {
      if (organized[threat.agent_id]) organized[threat.agent_id].killed.push(threat);
    });

    return organized;
  }, [agents, routerData, mlData, supplyData, killedData]);

  // FILTERS & STATS
  const filteredAgents = useMemo(() => {
    let list = Object.values(threatsByAgent);
    if (searchAgent) {
      list = list.filter(a => 
        a.hostname?.toLowerCase().includes(searchAgent.toLowerCase()) || 
        a.agent_id?.includes(searchAgent)
      );
    }
    if (selectedLicense !== 'all') {
      list = list.filter(a => a.license === selectedLicense);
    }
    return list;
  }, [threatsByAgent, searchAgent, selectedLicense]);

  const stats = useMemo(() => {
    const rCount = routerData?.alerts?.length || 0;
    const mCount = mlData?.alerts?.length || 0;
    const sCount = supplyData?.alerts?.length || 0;
    const kCount = killedData?.threats?.length || 0;
    
    const all = [...(routerData?.alerts || []), ...(mlData?.alerts || []), ...(supplyData?.alerts || [])];

    return {
      routerTotal: rCount,
      mlTotal: mCount,
      supplyTotal: sCount,
      killedTotal: kCount,
      criticalCount: all.filter(t => t.severity === 'CRITICAL').length,
      highCount: all.filter(t => t.severity === 'HIGH').length,
      totalActive: rCount + mCount + sCount,
      totalOverall: rCount + mCount + sCount + kCount
    };
  }, [routerData, mlData, supplyData, killedData]);

  const licenses = useMemo(() => {
    const unique = new Set(agents.map(a => a.license_package).filter(Boolean));
    return Array.from(unique);
  }, [agents]);

  const isPageLoading = agentsLoading || routerLoading || mlLoading || supplyLoading || killedLoading;

  // --- RENDER HELPERS ---

  const renderRouterAlerts = () => {
    const activeThreats = filteredAgents.filter(a => a.router.length > 0);
    if (activeThreats.length === 0) return <NoData icon={<Router />} label="No router threats detected" />;

    return activeThreats.map(agent => (
      <div key={agent.agent_id} className="mb-6 bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <AgentHeader agent={agent} count={agent.router.length} type="Network" color="red" />
        <div className="p-4 space-y-3">
          {agent.router.map(alert => (
            <div key={alert.id} className="flex justify-between items-center p-3 bg-red-50/30 rounded border border-red-100">
              <div>
                <h5 className="font-bold text-slate-800 text-sm">{alert.threat_type}</h5>
                <p className="text-xs text-slate-500">{alert.threat_vector} | Source: {alert.source_ip || 'N/A'}</p>
              </div>
              <SeverityBadge severity={alert.severity} />
            </div>
          ))}
        </div>
      </div>
    ));
  };

  const renderMLEvasion = () => {
    const activeThreats = filteredAgents.filter(a => a.ml.length > 0);
    if (activeThreats.length === 0) return <NoData icon={<BrainCircuit />} label="No AI evasion detected" />;

    return activeThreats.map(agent => (
      <div key={agent.agent_id} className="mb-6 bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <AgentHeader agent={agent} count={agent.ml.length} type="AI Evasion" color="purple" />
        <div className="p-4 space-y-3">
          {agent.ml.map(alert => (
            <div key={alert.id} className="flex justify-between items-center p-3 bg-purple-50/30 rounded border border-purple-100">
              <div>
                <h5 className="font-bold text-slate-800 text-sm">{alert.evasion_type}</h5>
                <p className="text-xs text-slate-500">{alert.malware_family} | Confidence: {(alert.confidence * 100).toFixed(1)}%</p>
              </div>
              <SeverityBadge severity={alert.severity} />
            </div>
          ))}
        </div>
      </div>
    ));
  };

  const renderSupplyChain = () => {
    const activeThreats = filteredAgents.filter(a => a.supply.length > 0);
    if (activeThreats.length === 0) return <NoData icon={<Package />} label="No supply chain threats" />;

    return activeThreats.map(agent => (
      <div key={agent.agent_id} className="mb-6 bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <AgentHeader agent={agent} count={agent.supply.length} type="Supply Chain" color="amber" />
        <div className="p-4 space-y-3">
          {agent.supply.map(alert => (
            <div key={alert.id} className="flex justify-between items-center p-3 bg-amber-50/30 rounded border border-amber-100">
              <div>
                <h5 className="font-bold text-slate-800 text-sm">{alert.package_name}</h5>
                <p className="text-xs text-slate-500">{alert.type} | {alert.manager} by {alert.user}</p>
              </div>
              <span className={`px-2 py-1 rounded text-[10px] font-bold ${alert.blocked ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {alert.blocked ? 'BLOCKED' : 'DETECTED'}
              </span>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  const renderKilledThreats = () => {
    const activeThreats = filteredAgents.filter(a => a.killed.length > 0);
    if (activeThreats.length === 0) return <NoData icon={<Shield />} label="No blocked threats history" />;

    return activeThreats.map(agent => (
      <div key={agent.agent_id} className="mb-6 bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <AgentHeader agent={agent} count={agent.killed.length} type="Blocked" color="green" />
        <div className="p-4 space-y-3">
          {agent.killed.map(threat => (
            <div key={threat.id} className="flex justify-between items-center p-3 bg-green-50/30 rounded border border-green-100">
              <div>
                <h5 className="font-bold text-slate-800 text-sm">{threat.threat_name}</h5>
                <p className="text-xs text-slate-500">Method: {threat.killed_by} at {new Date(threat.killed_at).toLocaleString()}</p>
              </div>
              <span className="text-[10px] font-bold text-green-700 uppercase tracking-tight">Neutralized</span>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div className="space-y-6 pb-10">
      {/* 1. Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center">
          <AlertTriangle className="w-8 h-8 mr-3 text-orange-600" />
          Advanced Threat Detection
        </h1>
        <p className="text-sm text-slate-500 mt-1">Cross-fleet monitoring for Network, AI Evasion, and Supply Chain attacks</p>
      </div>

      {/* 2. Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="CRITICAL" value={stats.criticalCount} color="red" icon={<AlertTriangle />} />
        <StatCard label="HIGH RISK" value={stats.highCount} color="orange" icon={<TrendingUp />} />
        <StatCard label="ACTIVE" value={stats.totalActive} color="blue" icon={<Router />} />
        <StatCard label="BLOCKED" value={stats.killedTotal} color="green" icon={<Shield />} />
      </div>

      {/* 3. Global Filters */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 items-end">
        <div className="flex-1 w-full">
          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-2">Search Fleet</label>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search hostname or agent ID..." 
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              value={searchAgent}
              onChange={(e) => setSearchAgent(e.target.value)}
            />
          </div>
        </div>
        <div className="w-full md:w-64">
          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-2">Filter by License</label>
          <select 
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none"
            value={selectedLicense}
            onChange={(e) => setSelectedLicense(e.target.value)}
          >
            <option value="all">All Packages</option>
            {licenses.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>

      {/* 4. Attack Vector Tabs */}
      <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl w-fit">
        <TabButton active={activeTab === 'router'} label="Network" count={stats.routerTotal} onClick={() => setActiveTab('router')} />
        <TabButton active={activeTab === 'ml'} label="AI Evasion" count={stats.mlTotal} onClick={() => setActiveTab('ml')} />
        <TabButton active={activeTab === 'supply'} label="Supply Chain" count={stats.supplyTotal} onClick={() => setActiveTab('supply')} />
        <TabButton active={activeTab === 'killed'} label="Blocked" count={stats.killedTotal} onClick={() => setActiveTab('killed')} />
      </div>

      {/* 5. Threat Content */}
      <div className="min-h-[400px]">
        {isPageLoading ? (
           <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mb-4" />
              <p className="text-sm font-mono tracking-widest">SYNCHRONIZING THREAT INTELLIGENCE...</p>
           </div>
        ) : (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
            {activeTab === 'router' && renderRouterAlerts()}
            {activeTab === 'ml' && renderMLEvasion()}
            {activeTab === 'supply' && renderSupplyChain()}
            {activeTab === 'killed' && renderKilledThreats()}
          </div>
        )}
      </div>
    </div>
  );
};

// --- SUBCOMPONENTS ---

const StatCard = ({ label, value, color, icon }) => (
  <div className={`p-4 rounded-xl border border-${color}-200 bg-${color}-50/50 flex justify-between items-center`}>
    <div>
      <p className={`text-[10px] font-bold text-${color}-600 uppercase tracking-widest`}>{label}</p>
      <p className={`text-2xl font-black text-${color}-700`}>{value}</p>
    </div>
    <div className={`text-${color}-500 opacity-20`}>{icon}</div>
  </div>
);

const TabButton = ({ active, label, count, onClick }) => (
  <button 
    onClick={onClick}
    className={`px-6 py-2 rounded-lg text-xs font-bold transition-all ${
      active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
    }`}
  >
    {label} <span className="ml-1 opacity-50">{count}</span>
  </button>
);

const AgentHeader = ({ agent, count, type, color }) => (
  <div className={`px-4 py-3 border-b border-${color}-100 bg-${color}-50/50 flex justify-between items-center`}>
    <div>
      <span className="text-sm font-bold text-slate-800">{agent.hostname}</span>
      <span className="ml-3 text-[10px] font-mono text-slate-400">{agent.agent_id.slice(0,12)}...</span>
    </div>
    <span className={`text-[10px] font-bold bg-${color}-100 text-${color}-700 px-2 py-0.5 rounded-full uppercase`}>
      {count} {type} Alerts
    </span>
  </div>
);

const SeverityBadge = ({ severity }) => {
  const colors = {
    CRITICAL: 'bg-red-600 text-white',
    HIGH: 'bg-orange-500 text-white',
    MEDIUM: 'bg-blue-500 text-white',
    LOW: 'bg-slate-400 text-white'
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${colors[severity] || colors.LOW}`}>{severity}</span>;
};

const NoData = ({ icon, label }) => (
  <div className="flex flex-col items-center justify-center py-20 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400">
    <div className="mb-3 opacity-20">{React.cloneElement(icon, { size: 48 })}</div>
    <p className="text-sm font-medium">{label}</p>
  </div>
);

const Search = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
);

export default DetectionAlertsPage;